import React, { useState, useRef } from 'react';
import { useAuth } from '@/App';
import { useApiData, useApiMutation } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  FileText, Calculator, BarChart3, TrendingUp, DollarSign, Users, Shield, 
  Zap, Target, ArrowUpRight, ArrowDownRight, Download, RefreshCw,
  CheckCircle2, AlertTriangle, AlertCircle, Sparkles, PiggyBank, Building2
} from 'lucide-react';
import { PageHeader } from '@/components/ModernUI';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import DataFixToolbar from '@/components/common/DataFixToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Executive One-Pager Component
const ExecutiveSummary = ({ token }) => {
  const summaryRef = useRef(null);
  
  const { data: summary, loading, refetch } = useApiData('/api/roi/executive-summary', {
    cache: true,
    cacheKey: 'roi_summary'
  });

  const exportPDF = async () => {
    if (!summaryRef.current) return;
    
    toast.loading('Generating PDF...');
    try {
      const canvas = await html2canvas(summaryRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`Executive_Summary_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.dismiss();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
  };

  if (loading) return <Skeleton className="h-96 w-full" />;
  if (!summary) return null;

  const getStatusIcon = (status) => {
    if (status === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    return <AlertCircle className="h-4 w-4 text-red-500" />;
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={exportPDF} className="gap-2" data-testid="export-pdf-btn">
          <Download className="h-4 w-4" /> Export PDF
        </Button>
      </div>

      <div ref={summaryRef} className="space-y-6 bg-white dark:bg-slate-900 p-6 rounded-xl">
        {/* Header */}
        <div className="text-center border-b pb-6">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{summary.organization}</h1>
          <p className="text-lg text-muted-foreground">Executive One-Pager | {summary.report_period}</p>
          <p className="text-sm text-muted-foreground mt-1">Generated: {new Date(summary.generated_at).toLocaleDateString()}</p>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Census */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900 dark:text-blue-100">Census</span>
              </div>
              <p className="text-3xl font-bold">{summary.key_metrics.census.current_adc}</p>
              <p className="text-sm text-muted-foreground">Target: {summary.key_metrics.census.target_adc}</p>
              <div className="flex items-center gap-1 mt-2">
                {summary.key_metrics.census.trend_direction === 'up' ? 
                  <ArrowUpRight className="h-4 w-4 text-emerald-500" /> : 
                  <ArrowDownRight className="h-4 w-4 text-red-500" />
                }
                <span className={summary.key_metrics.census.trend_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {summary.key_metrics.census.trend_percent}%
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Financial */}
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold text-emerald-900 dark:text-emerald-100">Financial</span>
              </div>
              <p className="text-3xl font-bold">{summary.key_metrics.financial.net_margin_percent}%</p>
              <p className="text-sm text-muted-foreground">Net Margin</p>
              <p className="text-xs mt-2">{formatCurrency(summary.key_metrics.financial.quarterly_revenue)} QTD</p>
            </CardContent>
          </Card>

          {/* Quality */}
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200 dark:border-violet-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-violet-600" />
                <span className="font-semibold text-violet-900 dark:text-violet-100">Quality</span>
              </div>
              <p className="text-3xl font-bold">{summary.key_metrics.quality.overall_health}%</p>
              <p className="text-sm text-muted-foreground">Overall Health</p>
              <p className="text-xs mt-2">Clinical: {summary.key_metrics.quality.clinical_score}%</p>
            </CardContent>
          </Card>

          {/* Workforce */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-900 dark:text-amber-100">Workforce</span>
              </div>
              <p className="text-3xl font-bold">{summary.key_metrics.workforce.total_staff}</p>
              <p className="text-sm text-muted-foreground">Total Staff</p>
              <p className="text-xs mt-2">{summary.key_metrics.workforce.utilization_rate}% Utilization</p>
            </CardContent>
          </Card>
        </div>

        {/* Highlights */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Performance Highlights</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summary.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-start gap-2 p-3 rounded-lg bg-muted/50">
                  {getStatusIcon(highlight.status)}
                  <div>
                    <p className="font-medium text-sm">{highlight.category}</p>
                    <p className="text-xs text-muted-foreground">{highlight.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Priority Action Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary.action_items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border">
                  <Badge className={item.priority === 'high' ? 'bg-red-100 text-red-700' : item.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                    {item.priority}
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium">{item.action}</p>
                    <p className="text-sm text-muted-foreground">{item.category} • {item.impact}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ROI Calculator Component
const ROICalculator = ({ token }) => {
  const [calculatorData, setCalculatorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState({
    current_adc: 178,
    target_adc: 200,
    current_staff: 45,
    overtime_percent: 10,
    turnover_rate: 15,
    avg_salary: 75000,
    compliance_risk: 50000
  });
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const fetchCalculatorData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/roi/calculator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalculatorData(data);
        // Initialize inputs from current state
        setInputs(prev => ({
          ...prev,
          current_adc: data.current_state.adc,
          current_staff: data.current_state.staff_count
        }));
      }
    } catch (error) {
      console.error('Failed to fetch calculator data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCalculatorData(); }, [fetchCalculatorData]);

  const calculateROI = async () => {
    setCalculating(true);
    try {
      const res = await fetch(`${API_URL}/api/roi/calculator/calculate`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inputs)
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        toast.success('ROI calculated successfully');
      }
    } catch (error) {
      toast.error('Failed to calculate ROI');
    } finally {
      setCalculating(false);
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

  if (loading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-500" />
            ROI Calculator Inputs
          </CardTitle>
          <CardDescription>Adjust values to calculate potential ROI</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Census Inputs */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground">Census</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Current ADC</Label>
                <Input 
                  type="number" 
                  value={inputs.current_adc} 
                  onChange={(e) => setInputs({...inputs, current_adc: parseInt(e.target.value) || 0})} 
                />
              </div>
              <div>
                <Label>Target ADC</Label>
                <Input 
                  type="number" 
                  value={inputs.target_adc} 
                  onChange={(e) => setInputs({...inputs, target_adc: parseInt(e.target.value) || 0})} 
                />
              </div>
            </div>
          </div>

          {/* Staffing Inputs */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground">Staffing</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Staff Count</Label>
                <Input 
                  type="number" 
                  value={inputs.current_staff} 
                  onChange={(e) => setInputs({...inputs, current_staff: parseInt(e.target.value) || 0})} 
                />
              </div>
              <div>
                <Label>Avg Salary ($)</Label>
                <Input 
                  type="number" 
                  value={inputs.avg_salary} 
                  onChange={(e) => setInputs({...inputs, avg_salary: parseInt(e.target.value) || 0})} 
                />
              </div>
            </div>
            
            <div>
              <Label>Overtime Rate: {inputs.overtime_percent}%</Label>
              <Slider 
                value={[inputs.overtime_percent]} 
                onValueChange={([val]) => setInputs({...inputs, overtime_percent: val})}
                max={30}
                step={1}
                className="mt-2"
              />
            </div>
            
            <div>
              <Label>Annual Turnover Rate: {inputs.turnover_rate}%</Label>
              <Slider 
                value={[inputs.turnover_rate]} 
                onValueChange={([val]) => setInputs({...inputs, turnover_rate: val})}
                max={40}
                step={1}
                className="mt-2"
              />
            </div>
          </div>

          {/* Compliance Inputs */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground">Compliance</h4>
            <div>
              <Label>Annual Compliance Risk ($)</Label>
              <Input 
                type="number" 
                value={inputs.compliance_risk} 
                onChange={(e) => setInputs({...inputs, compliance_risk: parseInt(e.target.value) || 0})} 
              />
            </div>
          </div>

          <Button onClick={calculateROI} disabled={calculating} className="w-full">
            {calculating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
            Calculate ROI
          </Button>
        </CardContent>
      </Card>

      {/* Results Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="h-5 w-5 text-emerald-500" />
            ROI Results
          </CardTitle>
          <CardDescription>Projected annual savings and ROI</CardDescription>
        </CardHeader>
        <CardContent>
          {!results ? (
            <div className="text-center py-12">
              <Calculator className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Adjust inputs and click Calculate to see results</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Total ROI */}
              <div className="text-center p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                <p className="text-sm text-muted-foreground">Total Annual ROI</p>
                <p className="text-4xl font-bold text-emerald-600">{formatCurrency(results.total_annual_roi)}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {formatCurrency(results.monthly_roi)}/month • {results.roi_multiple}x ROI
                </p>
                <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                  Payback in {results.payback_months} months
                </Badge>
              </div>

              {/* Breakdown */}
              <div className="space-y-3">
                <h4 className="font-semibold">ROI Breakdown</h4>
                
                <div className="p-3 rounded-lg border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Staffing Optimization</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(results.roi_breakdown.staffing_optimization.subtotal)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Overtime Savings: {formatCurrency(results.roi_breakdown.staffing_optimization.overtime_savings)}</p>
                    <p>Turnover Prevention: {formatCurrency(results.roi_breakdown.staffing_optimization.turnover_prevention)}</p>
                  </div>
                </div>

                <div className="p-3 rounded-lg border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Census Growth</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(results.roi_breakdown.census_growth.subtotal)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    +{results.roi_breakdown.census_growth.additional_patients} patients × {formatCurrency(results.roi_breakdown.census_growth.revenue_per_patient_year)}/year
                  </p>
                </div>

                <div className="p-3 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Compliance Risk Reduction</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(results.roi_breakdown.compliance_risk_reduction.risk_mitigation)}</span>
                  </div>
                </div>

                <div className="p-3 rounded-lg border">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Operational Efficiency</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(results.roi_breakdown.operational_efficiency.productivity_gains)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ROI Dashboard Component
const ROIDashboard = ({ token }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/roi/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);

  if (loading) return <Skeleton className="h-96 w-full" />;
  if (!dashboard) return null;

  return (
    <div className="space-y-6">
      {/* Total Realized ROI */}
      <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100">Total Realized ROI ({dashboard.period})</p>
              <p className="text-4xl font-bold">{formatCurrency(dashboard.total_realized_roi)}</p>
            </div>
            <PiggyBank className="h-16 w-16 text-emerald-200" />
          </div>
        </CardContent>
      </Card>

      {/* KPI Improvements */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              <span className="font-medium">Margin Improvement</span>
            </div>
            <p className="text-2xl font-bold">{dashboard.kpi_improvements.margin_improvement.current}%</p>
            <div className="flex items-center gap-1 text-sm">
              {dashboard.kpi_improvements.margin_improvement.change >= 0 ? 
                <ArrowUpRight className="h-4 w-4 text-emerald-500" /> :
                <ArrowDownRight className="h-4 w-4 text-red-500" />
              }
              <span className={dashboard.kpi_improvements.margin_improvement.change >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {dashboard.kpi_improvements.margin_improvement.change > 0 ? '+' : ''}{dashboard.kpi_improvements.margin_improvement.change}%
              </span>
              <span className="text-muted-foreground">vs. prior</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Compliance Score</span>
            </div>
            <p className="text-2xl font-bold">{dashboard.kpi_improvements.compliance_improvement.current}%</p>
            <div className="flex items-center gap-1 text-sm">
              {dashboard.kpi_improvements.compliance_improvement.change >= 0 ? 
                <ArrowUpRight className="h-4 w-4 text-emerald-500" /> :
                <ArrowDownRight className="h-4 w-4 text-red-500" />
              }
              <span className={dashboard.kpi_improvements.compliance_improvement.change >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {dashboard.kpi_improvements.compliance_improvement.change > 0 ? '+' : ''}{dashboard.kpi_improvements.compliance_improvement.change}%
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-5 w-5 text-amber-500" />
              <span className="font-medium">Productivity</span>
            </div>
            <p className="text-2xl font-bold">{dashboard.kpi_improvements.productivity_improvement.current}%</p>
            <p className="text-sm text-muted-foreground">Current Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Performance Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">Month</th>
                  <th className="text-right py-2 font-medium">Revenue</th>
                  <th className="text-right py-2 font-medium">Expenses</th>
                  <th className="text-right py-2 font-medium">Margin</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.monthly_trend.map((month, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-2 font-medium">{month.month}</td>
                    <td className="py-2 text-right text-emerald-600">{formatCurrency(month.revenue)}</td>
                    <td className="py-2 text-right text-rose-600">{formatCurrency(month.expenses)}</td>
                    <td className="py-2 text-right">
                      <Badge className={month.margin >= 12 ? 'bg-emerald-100 text-emerald-700' : month.margin >= 8 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}>
                        {month.margin?.toFixed(1)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main ROI & One Pager Page
export default function ROIOnePager() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('summary');

  return (
    <div className="space-y-6" data-testid="roi-onepager-page">
      {/* Futuristic Neon Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10">
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
              <BarChart3 className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
            </div>
            <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
              ROI & Executive Summary
            </span>
          </h1>
          <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
            One-pager insights, ROI calculator, and performance dashboard
          </p>
        </div>
      </div>

      {/* Data Fix Toolbar */}
      <DataFixToolbar 
        onDataFixed={() => window.location.reload()} 
        variant="compact"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="summary" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <FileText className="h-4 w-4" /> One-Pager
          </TabsTrigger>
          <TabsTrigger value="calculator" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Calculator className="h-4 w-4" /> ROI Calculator
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <BarChart3 className="h-4 w-4" /> ROI Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <ExecutiveSummary token={token} />
        </TabsContent>

        <TabsContent value="calculator">
          <ROICalculator token={token} />
        </TabsContent>

        <TabsContent value="dashboard">
          <ROIDashboard token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
